import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../hooks';
import IconSVG from '../UI/IconSVG';
import NotificationList from './NotificationList';
import NotificationBadge from './NotificationBadge';

interface NotificationDropdownProps {
  maxHeight?: number;
  className?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  maxHeight = 500,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    isRealTimeConnected,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Load notifications when first opened
  useEffect(() => {
    if (isOpen && !isRealTimeConnected && notifications.length === 0) {
      loadNotifications(20); // Load initial 20 notifications only if real-time not connected
    }
  }, [isOpen, isRealTimeConnected, notifications.length, loadNotifications]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Handle clicking "Load more"
  const handleLoadMore = () => {
    loadMoreNotifications(10);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Notification bell button with badge */}
      <NotificationBadge>
        <button
          ref={buttonRef}
          onClick={toggleDropdown}
          aria-expanded={isOpen}
          aria-controls="notification-dropdown"
          aria-label={`Notifications ${unreadCount ? ` (${unreadCount} unread)` : ''}`}
          className="relative p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <IconSVG icon="bell" className="w-6 h-6" />
        </button>
      </NotificationBadge>

      {/* Dropdown content */}
      {isOpen && (
        <div
          id="notification-dropdown"
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-80 sm:w-96 z-50 shadow-lg rounded-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 overflow-hidden"
          style={{ height: `${maxHeight}px` }}
        >
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onDelete={deleteNotification}
            className="h-full"
          />
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;